import { useEffect, useRef } from "react";
import { FieldValues, UseFormHandleSubmit, UseFormWatch } from "react-hook-form";

interface UseNurseFormAutoSaveProps<T extends FieldValues> {
    enabled: boolean;
    baselineData: T;
    watch: UseFormWatch<T>;
    handleSubmit: UseFormHandleSubmit<T>;
    onSubmit: (data: T) => void | Promise<void>;
    delayMs?: number;
}

export const useNurseFormAutoSave = <T extends FieldValues>({
    enabled,
    baselineData,
    watch,
    handleSubmit: _handleSubmit,
    onSubmit,
    delayMs = 3000,
}: UseNurseFormAutoSaveProps<T>) => {
    const baselineSnapshot = JSON.stringify(baselineData);
    const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const lastSavedSnapshotRef = useRef<string>(baselineSnapshot);
    const lastChangedFieldRef = useRef<string | undefined>(undefined);
    const latestValueRef = useRef<T>(baselineData);
    const onSubmitRef = useRef(onSubmit);
    const isSavingRef = useRef(false);

    const dispatchStatus = (state: "saving" | "saved", fieldName?: string) => {
        window.dispatchEvent(new CustomEvent("nurseform-autosave-status", {
            detail: {
                state,
                fieldName,
                timestamp: new Date().toLocaleTimeString([], { hour: "numeric", minute: "2-digit" }),
            },
        }));
    };

    const flushPendingSave = () => {
        const submittedSnapshot = JSON.stringify(latestValueRef.current);
        if (isSavingRef.current || submittedSnapshot === lastSavedSnapshotRef.current) return;

        isSavingRef.current = true;
        dispatchStatus("saving", lastChangedFieldRef.current);

        void Promise.resolve(onSubmitRef.current(latestValueRef.current)).then(() => {
            lastSavedSnapshotRef.current = submittedSnapshot;
            dispatchStatus("saved", lastChangedFieldRef.current);
        }).finally(() => {
            isSavingRef.current = false;
        });
    };

    useEffect(() => {
        onSubmitRef.current = onSubmit;
    }, [onSubmit]);

    useEffect(() => {
        if (baselineSnapshot !== lastSavedSnapshotRef.current) {
            latestValueRef.current = baselineData;
            lastSavedSnapshotRef.current = baselineSnapshot;
        }
    }, [baselineData, baselineSnapshot]);

    useEffect(() => {
        if (!enabled) {
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
                timeoutRef.current = null;
            }
            flushPendingSave();
            return;
        }

        const subscription = watch((value, info) => {
            latestValueRef.current = value as T;
            const nextSnapshot = JSON.stringify(value);
            if (nextSnapshot === lastSavedSnapshotRef.current) return;

            lastChangedFieldRef.current = info.name;

            if (timeoutRef.current) clearTimeout(timeoutRef.current);

            timeoutRef.current = setTimeout(() => {
                timeoutRef.current = null;
                flushPendingSave();
            }, delayMs);
        });

        return () => {
            subscription.unsubscribe();
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
                timeoutRef.current = null;
            }
            flushPendingSave();
        };
    }, [delayMs, enabled, watch]);
};
